import React, { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'
import {
  MEASURED_PARAMETERS,
  Rule,
  RuleOperator,
  RuleSet,
  RuleValueType,
  Severity,
} from '../../modules/protocols/models/Protocol'
import { operatorLabel, parameterLabel, severityLabel } from './labels'

type Props = {
  ruleSet: RuleSet
  editable: boolean
  onAddRule: (r: Partial<Rule>) => void
  onRemoveRule: (id: string) => void
  onRemoveRuleSet: (id: string) => void
}

// Scope of the rule: a single measurement, the last N or a window of time.
// They are mutually exclusive, as the backend validates.
type Scope = 'last' | 'samples' | 'interval'

const OPERATORS: RuleOperator[] = [
  RuleOperator.Equal,
  RuleOperator.NotEqual,
  RuleOperator.Greater,
  RuleOperator.Lower,
  RuleOperator.GreaterEqual,
  RuleOperator.LowerEqual,
  RuleOperator.TrendUp,
  RuleOperator.TrendDown,
]

const isTrend = (o: RuleOperator) => o === RuleOperator.TrendUp || o === RuleOperator.TrendDown

export function RuleEditor(props: Props) {
  const { t } = useTranslation()
  const { ruleSet, editable } = props

  const [parameter, setParameter] = useState<string>(MEASURED_PARAMETERS[0])
  const [freeParameter, setFreeParameter] = useState<string>('')
  const [operator, setOperator] = useState<RuleOperator>(RuleOperator.Greater)
  const [valueType, setValueType] = useState<RuleValueType>(RuleValueType.Num)
  const [value, setValue] = useState<string>('')
  const [scope, setScope] = useState<Scope>('last')
  const [amount, setAmount] = useState<string>('5')

  const finalParameter = parameter === '__form__' ? freeParameter.trim() : parameter

  const canAdd = finalParameter !== '' && (isTrend(operator) || value.trim() !== '')

  const add = () => {
    if (!canAdd) return

    const r: Partial<Rule> = {
      ruleSetID: ruleSet.id,
      parameter: finalParameter,
      operator: operator,
      valueType: valueType,
      value: value.trim(),
    }
    if (scope === 'samples') r.sampleSize = Number(amount) || 1
    if (scope === 'interval') r.intervalMinutes = Number(amount) || 60

    props.onAddRule(r)
    setValue('')
  }

  const describeScope = (r: Rule) => {
    if (r.sampleSize) return `${t('lastNMeasures', { n: r.sampleSize })}`
    if (r.intervalMinutes) return `${r.intervalMinutes} min`
    return t('lastMeasure')
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="h6">{ruleSet.name}</Typography>
        <Chip
          size="small"
          label={severityLabel(t, ruleSet.severity)}
          color={ruleSet.severity === Severity.Alarm ? 'error' : 'warning'}
        />
        <Box flexGrow={1} />
        {editable && (
          <IconButton size="small" onClick={() => props.onRemoveRuleSet(ruleSet.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" mb={2}>
        {ruleSet.notificationMessage}
      </Typography>

      {(ruleSet.rules || []).map((r) => (
        <Box key={r.id} display="flex" alignItems="center" gap={1} mb={1}>
          <Chip size="small" variant="outlined" label={parameterLabel(t, r.parameter)} />
          <Typography variant="body2">{operatorLabel(t, r.operator)}</Typography>
          {!isTrend(r.operator) && <Typography variant="body2">{r.value}</Typography>}
          <Chip size="small" label={describeScope(r)} />
          <Box flexGrow={1} />
          {editable && (
            <IconButton size="small" onClick={() => props.onRemoveRule(r.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ))}

      {(ruleSet.rules || []).length === 0 && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          {t('noRulesYet')}
        </Typography>
      )}

      {editable && (
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mt={2}>
          <Select
            size="small"
            value={parameter}
            onChange={(e) => setParameter(e.target.value as string)}
            sx={{ minWidth: 160 }}
          >
            {MEASURED_PARAMETERS.map((p) => (
              <MenuItem key={p} value={p}>
                {parameterLabel(t, p)}
              </MenuItem>
            ))}
            <MenuItem value="__form__">{t('formAnswer')}</MenuItem>
          </Select>

          {parameter === '__form__' && (
            <TextField
              size="small"
              placeholder="form:<formID>:<fieldID>"
              value={freeParameter}
              onChange={(e) => setFreeParameter(e.target.value)}
              sx={{ minWidth: 280 }}
            />
          )}

          <Select
            size="small"
            value={operator}
            onChange={(e) => setOperator(e.target.value as RuleOperator)}
            sx={{ minWidth: 170 }}
          >
            {OPERATORS.map((o) => (
              <MenuItem key={o} value={o}>
                {operatorLabel(t, o)}
              </MenuItem>
            ))}
          </Select>

          {!isTrend(operator) && (
            <Select
              size="small"
              value={valueType}
              onChange={(e) => setValueType(e.target.value as RuleValueType)}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value={RuleValueType.Num}>{t('numValue')}</MenuItem>
              <MenuItem value={RuleValueType.String}>{t('stringValue')}</MenuItem>
              <MenuItem value={RuleValueType.List}>{t('listValue')}</MenuItem>
            </Select>
          )}
          {!isTrend(operator) && (
            <TextField
              size="small"
              label={t('referenceValue')}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              sx={{ width: 140 }}
            />
          )}

          <Select
            size="small"
            value={scope}
            onChange={(e) => setScope(e.target.value as Scope)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="last">{t('lastMeasure')}</MenuItem>
            <MenuItem value="samples">{t('lastNMeasuresOption')}</MenuItem>
            <MenuItem value="interval">{t('timeWindow')}</MenuItem>
          </Select>

          {scope !== 'last' && (
            <TextField
              size="small"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              sx={{ width: 110 }}
            />
          )}

          <Button variant="outlined" onClick={add} disabled={!canAdd}>
            {t('addRule')}
          </Button>
        </Box>
      )}
    </Paper>
  )
}
